import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  View,
  TouchableOpacity,
  Modal,
  FlatList,
  Animated,
  Pressable,
  SafeAreaView,
  Platform,
  useWindowDimensions,
  StyleSheet,
} from "react-native";
import CustomDropdown from "../../../../../components/common/general/CustomDropDown/CustomDropdown";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CommentAvatar from "./CommentAvatar";
import type { Comment, CommentThread, CommentsApi, CommentTargetKind } from "./CommentsSection";
import { useTeamMembers, useCurrentUserId } from "../contexts/TeamMembersContext";
import type { TeamMember } from "../contexts/TeamMembersContext";
import { CheckCheck } from "lucide-react";

const ResolveAllIcon = CheckCheck as unknown as React.ComponentType<{ size?: number; color?: string }>;

interface ReviewableItem {
  id: string;
  title: string;
}

function getMember(
  comment: { authorId?: string; authorName?: string } | string,
  teamMembers: TeamMember[],
  fallbackName?: string
): TeamMember {
  if (typeof comment === "string") {
    return (
      teamMembers.find((m) => m.id === comment) ?? { id: comment, name: fallbackName || comment, role: "observer" }
    );
  }
  const found = teamMembers.find((m) => m.id === comment.authorId);
  if (found) return found;
  return {
    id: comment.authorId ?? "",
    name: comment.authorName ?? comment.authorId ?? "Unknown",
    role: "observer",
  };
}

type StatusFilter = "open" | "resolved" | "all";
const WHO_EVERYONE = "everyone";
const WHO_MINE = "mine";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function ResolveAllDialog({
  visible,
  openCount,
  onCancel,
  onConfirm,
}: {
  visible: boolean;
  openCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.dialogBackdrop} onPress={onCancel} />
      <View style={styles.dialogPositioner} pointerEvents="box-none">
        <View style={styles.dialogCard}>
          <CustomText text="Resolve all open comments?" useTranslationText={false} style={styles.dialogTitle} />
          <CustomText
            text={`This marks ${openCount} ${openCount === 1 ? "comment" : "comments"} as resolved by you. Each is recorded in the version history with a timestamp. You can reopen individual comments from the Resolved tab.`}
            useTranslationText={false}
            style={styles.dialogBody}
          />
          <View style={styles.dialogFooter}>
            <TouchableOpacity onPress={onCancel} style={styles.dialogSecondaryBtn} activeOpacity={0.7}>
              <CustomText text="Cancel" useTranslationText={false} style={styles.dialogSecondaryBtnText} />
            </TouchableOpacity>
            <TouchableOpacity onPress={onConfirm} style={styles.dialogPrimaryBtn} activeOpacity={0.8}>
              <CustomText text="Resolve all" useTranslationText={false} style={styles.dialogPrimaryBtnText} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function EmptyState({ statusFilter, whoFilter }: { statusFilter: StatusFilter; whoFilter: string }) {
  const teamMembers = useTeamMembers();
  const message =
    whoFilter === WHO_MINE
      ? "Nothing assigned to you here. Comments show up when a teammate routes one your way \u2014 assign or reassign from any thread."
      : whoFilter !== WHO_EVERYONE
        ? `No comments from ${getMember(whoFilter, teamMembers).name} match this filter.`
        : statusFilter === "open"
          ? "No open comments. Tap any review item and use the comment pin to start a thread."
          : statusFilter === "resolved"
            ? "No resolved comments yet."
            : "No comments yet on this course.";
  return (
    <View style={styles.emptyState}>
      <CustomText text="💬" useTranslationText={false} style={styles.emptyIcon} />
      <CustomText text={message} useTranslationText={false} style={styles.emptyText} />
    </View>
  );
}

function ThreadRow({
  thread,
  itemTitle,
  onPress,
}: {
  thread: CommentThread;
  itemTitle: string | undefined;
  onPress: () => void;
}) {
  const teamMembers = useTeamMembers();
  const author = getMember(thread.root, teamMembers);
  const replyCount = thread.replies.length;
  const isResolved = thread.root.resolved;
  const kindLabel =
    thread.root.targetKind === "module"
      ? "Module"
      : thread.root.targetKind === "review-item"
        ? "Review item"
        : "Activity";
  const kindIcon = thread.root.targetKind === "module" ? "▤" : thread.root.targetKind === "review-item" ? "📄" : "📝";

  return (
    <TouchableOpacity onPress={onPress} style={styles.threadRow} activeOpacity={0.7}>
      <View style={[styles.anchorChip, !itemTitle && styles.anchorChipGhost]}>
        <CustomText
          text={`${kindIcon} ${
            itemTitle
              ? `${kindLabel} · ${itemTitle}`
              : thread.root.targetLabelFallback
                ? `Deleted ${kindLabel.toLowerCase()} · ${thread.root.targetLabelFallback}`
                : `Deleted ${kindLabel.toLowerCase()}`
          }`}
          useTranslationText={false}
          style={[styles.anchorChipText, !itemTitle && styles.anchorChipTextGhost]}
          numberOfLines={1}
        />
      </View>

      <View style={styles.threadRowBody}>
        <CommentAvatar member={author} size={32} />
        <View style={styles.threadRowContent}>
          <View style={styles.threadRowMeta}>
            <CustomText text={author.name} useTranslationText={false} style={styles.threadRowAuthor} />
            <CustomText
              text={formatTimestamp(thread.root.createdAt)}
              useTranslationText={false}
              style={styles.threadRowTime}
            />
            {isResolved ? (
              <View style={styles.badgeSuccess}>
                <View style={styles.badgeSuccessContent}>
                  <View style={styles.badgeSuccessIcon}>
                    <CustomText text="✓" useTranslationText={false} style={styles.badgeSuccessIconText} />
                  </View>
                  <CustomText text="Resolved" useTranslationText={false} style={styles.badgeSuccessText} />
                </View>
              </View>
            ) : replyCount > 0 ? (
              <View style={styles.badgeDefault}>
                <CustomText
                  text={`↩ ${replyCount} ${replyCount === 1 ? "reply" : "replies"}`}
                  useTranslationText={false}
                  style={styles.badgeDefaultText}
                />
              </View>
            ) : null}
          </View>
          <CustomText
            text={thread.root.body}
            useTranslationText={false}
            style={styles.threadRowBodyText}
            numberOfLines={2}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
}

interface GlobalCommentsSheetProps {
  open: boolean;
  onClose: () => void;
  api: CommentsApi;
  items: ReviewableItem[];
  onJumpToThread: (targetKind: CommentTargetKind, targetId: string) => void;
  canResolveAll?: boolean;
}

export function GlobalCommentsSheet({
  open,
  onClose,
  api,
  items,
  onJumpToThread,
  canResolveAll = true,
}: GlobalCommentsSheetProps) {
  const teamMembers = useTeamMembers();
  const currentUserId = useCurrentUserId();
  const { width: screenWidth } = useWindowDimensions();
  const sheetWidth = Math.min(460, screenWidth);

  const translateX = useRef(new Animated.Value(sheetWidth)).current;

  useEffect(() => {
    Animated.timing(translateX, {
      toValue: open ? 0 : sheetWidth,
      duration: 280,
      useNativeDriver: true,
    }).start();
  }, [open, sheetWidth]);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [whoFilter, setWhoFilter] = useState<string>(WHO_EVERYONE);
  const [showResolveAllModal, setShowResolveAllModal] = useState(false);

  const itemTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) map.set(item.id, item.title);
    return map;
  }, [items]);

  const allThreads = api.allThreads();

  const filtered = useMemo(() => {
    return allThreads.filter((t) => {
      if (statusFilter === "open" && t.root.resolved) return false;
      if (statusFilter === "resolved" && !t.root.resolved) return false;
      if (whoFilter === WHO_MINE) {
        if (t.root.assigneeId !== currentUserId) return false;
      } else if (whoFilter !== WHO_EVERYONE && t.root.authorId !== whoFilter) {
        return false;
      }
      return true;
    });
  }, [allThreads, statusFilter, whoFilter]);

  const assignedToMeCount = useMemo(
    () => allThreads.filter((t) => !t.root.resolved && t.root.assigneeId === currentUserId).length,
    [allThreads]
  );

  const whoFilterOptions = useMemo(
    () => [
      { value: WHO_EVERYONE, label: "Everyone" },
      { value: WHO_MINE, label: `Assigned to me${assignedToMeCount > 0 ? ` · ${assignedToMeCount}` : ""}` },
      ...teamMembers.map((m) => ({
        value: m.id,
        label: `${m.name}${m.id === currentUserId ? " (you)" : ""}`,
      })),
    ],
    [assignedToMeCount, currentUserId, teamMembers]
  );

  const openCount = allThreads.filter((t) => !t.root.resolved).length;
  const resolvedCount = allThreads.filter((t) => t.root.resolved).length;
  const isResolveAllDisabled = !canResolveAll;

  const handleResolveAll = () => {
    if (isResolveAllDisabled) return;
    for (const thread of allThreads) {
      if (!thread.root.resolved) api.resolve(thread.root.id);
    }
    setShowResolveAllModal(false);
  };

  if (!open) return null;

  return (
    <Modal
      visible={open}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent={Platform.OS === "android"}>
      <Pressable style={styles.sheetBackdrop} onPress={onClose} />

      <Animated.View style={[styles.sheet, { width: sheetWidth, transform: [{ translateX }] }]}>
        <SafeAreaView style={styles.sheetSafeArea}>
          <View style={styles.sheetHeader}>
            <View style={styles.sheetTitleRow}>
              <CustomText text="Comments" useTranslationText={false} style={styles.sheetTitle} />
              <TouchableOpacity onPress={onClose} style={styles.sheetCloseBtn} activeOpacity={0.7}>
                <CustomText text="✕" useTranslationText={false} style={styles.sheetCloseBtnText} />
              </TouchableOpacity>
            </View>

            <View style={styles.sheetSubtitleRow}>
              <CustomText
                text={`${openCount} open · ${resolvedCount} resolved`}
                useTranslationText={false}
                style={styles.sheetSubtitle}
              />
              {openCount > 0 && (
                <TouchableOpacity
                  disabled={isResolveAllDisabled}
                  onPress={() => {
                    if (isResolveAllDisabled) return;
                    setShowResolveAllModal(true);
                  }}
                  style={[styles.resolveAllBtn, isResolveAllDisabled && styles.resolveAllBtnDisabled]}
                  activeOpacity={0.7}>
                  <View style={styles.resolveAllBtnContent}>
                    <ResolveAllIcon size={12} color={isResolveAllDisabled ? "#98A2B3" : "#344054"} />
                    <CustomText
                      text="Resolve all open"
                      useTranslationText={false}
                      style={[styles.resolveAllBtnText, isResolveAllDisabled && styles.resolveAllBtnTextDisabled]}
                    />
                  </View>
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.filtersRow}>
              <View style={styles.segmentedControl}>
                {(["open", "resolved", "all"] as const).map((status) => (
                  <TouchableOpacity
                    key={status}
                    onPress={() => setStatusFilter(status)}
                    style={[styles.segmentedBtn, statusFilter === status && styles.segmentedBtnActive]}
                    activeOpacity={0.7}>
                    <CustomText
                      text={status.charAt(0).toUpperCase() + status.slice(1)}
                      useTranslationText={false}
                      style={[styles.segmentedBtnText, statusFilter === status && styles.segmentedBtnTextActive]}
                    />
                  </TouchableOpacity>
                ))}
              </View>

              <CustomDropdown
                options={whoFilterOptions}
                value={whoFilter}
                onChange={setWhoFilter}
                fullWidth={false}
                placeholder="Everyone"
                containerStyle={styles.whoFilterDropdown}
                dropdownStyle={styles.whoFilterDropdownField}
              />
            </View>
          </View>

          <FlatList
            data={filtered}
            keyExtractor={(t) => t.root.id}
            style={styles.threadList}
            ItemSeparatorComponent={() => <View style={styles.threadSeparator} />}
            ListEmptyComponent={<EmptyState statusFilter={statusFilter} whoFilter={whoFilter} />}
            renderItem={({ item: thread }) => (
              <ThreadRow
                thread={thread}
                itemTitle={thread.root.targetId ? itemTitleById.get(thread.root.targetId) : undefined}
                onPress={() => {
                  if (thread.root.targetKind && thread.root.targetId) {
                    onJumpToThread(thread.root.targetKind, thread.root.targetId);
                    onClose();
                  }
                }}
              />
            )}
          />

          <View style={styles.sheetFooter}>
            <TouchableOpacity onPress={onClose} style={styles.closeFooterBtn} activeOpacity={0.7}>
              <CustomText text="✕ Close" useTranslationText={false} style={styles.closeFooterBtnText} />
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Animated.View>

      <ResolveAllDialog
        visible={showResolveAllModal}
        openCount={openCount}
        onCancel={() => setShowResolveAllModal(false)}
        onConfirm={handleResolveAll}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  sheetBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.25)",
  },

  sheet: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#FFFFFF",
    shadowColor: "#000",
    shadowOffset: { width: -4, height: 0 },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 16,
  },
  sheetSafeArea: {
    flex: 1,
  },

  sheetHeader: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  sheetTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  sheetTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#101828",
  },
  sheetCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  sheetCloseBtnText: {
    fontSize: 14,
    color: "#344054",
    fontWeight: "600",
  },
  sheetSubtitleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  sheetSubtitle: {
    fontSize: 14,
    color: "#344054",
  },
  resolveAllBtn: {
    height: 30,
    paddingHorizontal: 10,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  resolveAllBtnDisabled: {
    opacity: 0.6,
  },
  resolveAllBtnContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  resolveAllBtnText: {
    fontSize: 12,
    color: "#344054",
    fontWeight: "500",
  },
  resolveAllBtnTextDisabled: {
    color: "#98A2B3",
  },

  filtersRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  segmentedControl: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    padding: 2,
  },
  segmentedBtn: {
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  segmentedBtnActive: {
    backgroundColor: "#F3F4F6",
  },
  segmentedBtnText: {
    fontSize: 12,
    fontWeight: "500",
    color: "#667085",
  },
  segmentedBtnTextActive: {
    color: "#101828",
  },

  whoFilterDropdown: {
    flex: 1,
    minWidth: 140,
  },
  whoFilterDropdownField: {
    height: 32,
    minHeight: 32,
    borderRadius: 8,
  },

  threadList: {
    flex: 1,
  },
  threadSeparator: {
    height: 1,
    backgroundColor: "#F3F4F6",
  },

  threadRow: {
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  anchorChip: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
    borderRadius: 6,
    paddingHorizontal: 8,
    height: 24,
    marginBottom: 8,
    maxWidth: "100%",
  },
  anchorChipGhost: {
    backgroundColor: "#F9FAFB",
  },
  anchorChipText: {
    fontSize: 11,
    fontWeight: "500",
    color: "#374151",
  },
  anchorChipTextGhost: {
    color: "#9CA3AF",
    fontStyle: "italic",
  },
  threadRowBody: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  threadRowContent: {
    flex: 1,
    minWidth: 0,
  },
  threadRowMeta: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 2,
  },
  threadRowAuthor: {
    fontSize: 14,
    fontWeight: "600",
    color: "#101828",
  },
  threadRowTime: {
    fontSize: 11,
    color: "#344054",
  },
  threadRowBodyText: {
    fontSize: 14,
    color: "#374151",
    lineHeight: 20,
  },

  badgeSuccess: {
    height: 18,
    paddingHorizontal: 6,
    borderRadius: 4,
    backgroundColor: "#F2FCEB",
    alignItems: "center",
    justifyContent: "center",
  },
  badgeSuccessContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  badgeSuccessIcon: {
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#4ba30d",
    alignItems: "center",
    justifyContent: "center",
  },
  badgeSuccessIconText: {
    fontSize: 7,
    lineHeight: 7,
    fontWeight: "700",
    color: "#4ba30d",
  },
  badgeSuccessText: {
    fontSize: 10,
    fontWeight: "600",
    color: "#4ba30d",
  },
  badgeDefault: {
    height: 18,
    paddingHorizontal: 6,
    borderRadius: 4,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
  },
  badgeDefaultText: {
    fontSize: 10,
    fontWeight: "500",
    color: "#4B5563",
  },

  emptyState: {
    paddingHorizontal: 24,
    paddingVertical: 48,
    alignItems: "center",
  },
  emptyIcon: {
    fontSize: 32,
    marginBottom: 12,
    opacity: 0.4,
  },
  emptyText: {
    fontSize: 14,
    color: "#344054",
    textAlign: "center",
    maxWidth: 280,
    lineHeight: 20,
  },

  sheetFooter: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#F3F4F6",
    alignItems: "flex-end",
  },
  closeFooterBtn: {
    height: 32,
    paddingHorizontal: 14,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  closeFooterBtnText: {
    fontSize: 13,
    fontWeight: "500",
    color: "#374151",
  },

  dialogBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  dialogPositioner: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  dialogCard: {
    width: "100%",
    maxWidth: 400,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 16,
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#101828",
    marginBottom: 10,
  },
  dialogBody: {
    fontSize: 14,
    color: "#4B5563",
    lineHeight: 20,
    marginBottom: 20,
  },
  dialogFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  dialogSecondaryBtn: {
    height: 36,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  dialogSecondaryBtnText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
  },
  dialogPrimaryBtn: {
    height: 36,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: "#7C3AED",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogPrimaryBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});
